import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle, Home, Copy, Mail, MessageCircle, AlertTriangle } from "lucide-react";
import { api, LOGO_URL } from "../lib/config";
import { toast } from "sonner";

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [code, setCode] = useState("");
  const [userName, setUserName] = useState("");

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let attempts = 0;
    const max = 60;
    const poll = async () => {
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid" || r.data.status === "complete") {
          setCode(r.data.participant_code || "");
          setUserName(r.data.user_name || "");
          setStatus("paid");
          return;
        }
        if (r.data.status === "expired") { setStatus("expired"); return; }
        attempts++;
        if (attempts >= max) { setStatus("failed"); return; }
        setTimeout(poll, 2000);
      } catch (e) {
        attempts++;
        if (attempts >= max) setStatus("failed");
        else setTimeout(poll, 2500);
      }
    };
    poll();
  }, [sessionId]);

  const copyCode = async () => {
    try { await navigator.clipboard.writeText(code); toast.success("Codice copiato!"); } catch {}
  };

  return (
    <div className="min-h-screen bg-fp-bg grid-bg flex items-center justify-center p-5 py-12" data-testid="payment-success-page">
      <div className="absolute inset-0 radial-glow opacity-40" />

      {status === "checking" && (
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
          className="relative max-w-lg w-full p-10 rounded-3xl border border-white/10 bg-fp-surface/60 backdrop-blur-xl text-center tracing-border">
          <img src={LOGO_URL} alt="" className="w-16 h-16 mx-auto" />
          <Loader2 className="mx-auto mt-8 text-fp-cyan animate-spin" size={56} />
          <h1 className="mt-6 font-heading font-black text-3xl silver-gradient">Verifico il pagamento...</h1>
          <p className="mt-3 text-white/60">Attendere, stiamo confermando la transazione.</p>
        </motion.div>
      )}

      {status === "paid" && (
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
          className="relative max-w-xl w-full">
          <div className="p-8 md:p-10 rounded-3xl border border-fp-cyan/40 bg-gradient-to-br from-fp-cyan/10 via-fp-surface/80 to-fp-surface/80 backdrop-blur-xl tracing-border">
            <div className="text-center">
              <img src={LOGO_URL} alt="" className="w-16 h-16 mx-auto" />
              <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", delay: 0.2 }}>
                <CheckCircle2 className="mx-auto mt-6 text-fp-cyan animate-pulse-glow" size={72} />
              </motion.div>
              <h1 className="mt-5 font-heading font-black text-3xl md:text-4xl silver-gradient" data-testid="payment-confirmed">
                Pagamento riuscito!
              </h1>
              <p className="mt-3 text-white/75">
                {userName ? `Grazie ${userName}! ` : ""}Sei ufficialmente iscritto al torneo{" "}
                <span className="text-fp-cyan font-semibold">Fanta Partenope 2026/2027</span>.
              </p>
            </div>

            {/* Participant code */}
            {code && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
                className="mt-8 p-6 rounded-2xl bg-fp-bg/70 border-2 border-fp-cyan/50 text-center relative overflow-hidden"
                data-testid="participant-code-box">
                <div className="absolute inset-0 shimmer opacity-20 pointer-events-none" />
                <div className="relative">
                  <div className="text-[10px] tracking-[0.4em] uppercase text-fp-cyan font-heading font-semibold">
                    Il tuo codice univoco
                  </div>
                  <div className="mt-3 font-heading font-black text-3xl md:text-4xl text-fp-cyan cyan-text-glow tracking-[0.15em]" data-testid="participant-code">
                    {code}
                  </div>
                  <button onClick={copyCode}
                    className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-fp-cyan/15 border border-fp-cyan/40 text-fp-cyan text-xs font-heading font-bold tracking-wider uppercase hover:bg-fp-cyan hover:text-fp-bg transition-all"
                    data-testid="copy-code-btn">
                    <Copy size={14} /> Copia codice
                  </button>
                </div>
              </motion.div>
            )}

            {/* Warning */}
            <div className="mt-6 p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/30 flex items-start gap-3">
              <AlertTriangle className="text-yellow-400 flex-shrink-0 mt-0.5" size={18} />
              <div className="text-sm text-yellow-100/90">
                <strong>Conserva questo codice — non perderlo.</strong> Dovrai esibirlo allo staff
                non appena verrai contattato. Una copia è stata inviata anche alla tua email.
              </div>
            </div>

            {/* Contacts */}
            <div className="mt-6 p-5 rounded-2xl bg-fp-bg/40 border border-white/10">
              <div className="text-[10px] tracking-[0.4em] uppercase text-fp-cyan font-heading font-semibold mb-3">
                In caso di problemi contattaci
              </div>
              <div className="space-y-2">
                <a href="mailto:fantapartenope@pec.it" className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 transition-colors" data-testid="contact-email">
                  <Mail size={18} className="text-fp-cyan" />
                  <span className="text-white text-sm">fantapartenope@pec.it</span>
                </a>
                <a href="https://wa.me/393778100717" target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 transition-colors" data-testid="contact-whatsapp">
                  <MessageCircle size={18} className="text-fp-cyan" />
                  <span className="text-white text-sm">+39 377 8100717</span>
                  <span className="text-white/40 text-xs">(WhatsApp)</span>
                </a>
              </div>
            </div>

            <div className="mt-7 text-center">
              <Link to="/"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-fp-cyan text-fp-bg font-heading font-bold tracking-wider uppercase text-sm hover:shadow-[0_0_25px_rgba(0,229,255,0.6)] transition-all"
                data-testid="back-home-btn">
                <Home size={16} /> Torna alla home
              </Link>
            </div>
          </div>
        </motion.div>
      )}

      {(status === "failed" || status === "expired") && (
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
          className="relative max-w-lg w-full p-10 rounded-3xl border border-white/10 bg-fp-surface/60 backdrop-blur-xl text-center">
          <img src={LOGO_URL} alt="" className="w-16 h-16 mx-auto" />
          <XCircle className="mx-auto mt-8 text-red-400" size={64} />
          <h1 className="mt-6 font-heading font-black text-3xl">Verifica non riuscita</h1>
          <p className="mt-3 text-white/60">
            Se il pagamento è stato addebitato, contattaci a <strong className="text-white">fantapartenope@pec.it</strong> o WhatsApp <strong className="text-white">+39 377 8100717</strong> con l'ID transazione.
          </p>
          <Link to="/register" className="inline-flex items-center gap-2 mt-8 px-6 py-3 rounded-full border border-fp-cyan text-fp-cyan font-heading font-bold text-sm uppercase tracking-wider hover:bg-fp-cyan hover:text-fp-bg transition-all">
            Riprova
          </Link>
        </motion.div>
      )}
    </div>
  );
}
